/**
 * Распознавание ошибок middle (ExceptionClientModel) в JSON-ответах REST.
 *
 * @see dev/contracts/middle_error_codes.md
 */

export type MiddleErrorResponse = {
  status: number
  code: number
  message: string
  developerMessage?: string
  moreInfoUrl?: string
  url: string
  type?: string
}

/**
 * `true`, если JSON — успешный ответ платформы (UniData / ResponseDocument).
 *
 * Сериализатор middleware (`UniDataSerializer`) всегда даёт `fields` с `fields.fields`
 * или маркер `hasFormError`; опционально — `ProcessInstances`, `clientData`, `formError`.
 */
export function isUniDataResponseDocument(body: unknown): boolean {
  if (body == null || typeof body !== 'object' || Array.isArray(body)) return false
  const root = body as Record<string, unknown>

  const fields = root.fields
  if (fields != null && typeof fields === 'object' && !Array.isArray(fields)) {
    const fieldsObj = fields as Record<string, unknown>
    if (
      fieldsObj.fields != null &&
      typeof fieldsObj.fields === 'object' &&
      !Array.isArray(fieldsObj.fields)
    ) {
      return true
    }
    if ('hasFormError' in fieldsObj) return true
  }

  if (
    ('ProcessInstances' in root || 'clientData' in root || 'formError' in root) &&
    fields != null &&
    typeof fields === 'object'
  ) {
    return true
  }

  return false
}

/**
 * `true`, если тело ответа — envelope ошибки middle, а не данные платформы (UniData).
 *
 * Обязательные поля контракта: `status`, `code`, `message`, `url`.
 * Поле `url` (request URI) отсутствует в успешных ответах upload/prepare.
 */
export function isMiddleErrorResponse(body: unknown): body is MiddleErrorResponse {
  if (body == null || typeof body !== 'object' || Array.isArray(body)) return false
  const obj = body as Record<string, unknown>

  if (isUniDataResponseDocument(obj)) return false

  const url = obj.url
  const message = obj.message
  const status = obj.status
  const code = obj.code

  if (typeof url !== 'string' || url.trim() === '') return false
  if (typeof message !== 'string') return false
  if (typeof status !== 'number' || !Number.isFinite(status)) return false
  if (code === undefined || code === null) return false
  if (!Number.isFinite(Number(code))) return false

  return true
}

/** Текст ошибки для UI: `message`, иначе `developerMessage`. */
export function getMiddleErrorMessage(body: Record<string, unknown>): string {
  const message = typeof body.message === 'string' ? body.message.trim() : ''
  if (message) return message
  const developerMessage =
    typeof body.developerMessage === 'string' ? body.developerMessage.trim() : ''
  return developerMessage || 'Ошибка сервера'
}

/** Сообщение для не-UniData ответа upload (generic exception без envelope и т.п.). */
export function getNonUniDataUploadResponseMessage(payload: unknown): string {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const obj = payload as Record<string, unknown>
    const message = typeof obj.message === 'string' ? obj.message.trim() : ''
    if (message) return message
    const keys = Object.keys(obj)
    if (keys.length) {
      return `Ответ загрузки не является UniData платформы (ключи: ${keys.join(', ')}).`
    }
  }
  return 'Ответ загрузки не является UniData платформы.'
}
